// Ported from files/snapdraw-design-kit/code/src/components/snapdraw/brand.tsx
// (Lovable reference implementation) — used by the Tailwind preview pages only.
// StatusChip renders its label through the label directly in the kit (never
// localized, even in the kit's own bilingual pass) — translated here via the
// shared status.* keys so it doesn't silently stay English when the rest of
// the screen switches to Spanish.
// 2026-08-28 (backlog LAUNCH-19): the source PNG was a 1024×1024, 1.6 MB
// file rendered everywhere at 32-96px — on the critical path of every
// screen in the app (auth, main app shell sidebar, paywall, legal pages,
// admin console). Re-exported at 384×384 (4x the largest actual display
// size, 96px, so still sharp on high-DPI screens) as WebP q90 — 13 KB, a
// ~120x reduction, no visible quality loss at any size this renders at.
// The separate copy in Supabase Storage (used by the email templates,
// `supabase/email-templates/README.md`) is untouched — different file,
// different concern.
import markSrc from "@/assets/snapdraw-mark.webp";
import { cn } from "@/lib/utils";
import { useT } from "@/lib/i18n";

export const SNAPDRAW_MARK_URL = markSrc;

export function BrandMark({ className, size = 44 }: { className?: string; size?: number }) {
  return (
    <span
      className={cn(
        "relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-xl",
        className,
      )}
      style={{ width: size, height: size }}
    >
      <span aria-hidden className="absolute inset-0 rounded-xl bg-arc/25 blur-lg" />
      <img
        src={SNAPDRAW_MARK_URL}
        alt="SnapDraw"
        width={size}
        height={size}
        className="relative h-full w-full rounded-xl object-cover"
      />
    </span>
  );
}

export function BrandLockup({ org, size = 44 }: { org?: string; size?: number }) {
  return (
    <div className="flex items-center gap-3">
      <BrandMark size={size} />
      <div className="leading-none">
        <div className="font-display text-2xl font-bold uppercase tracking-wide text-foreground">
          SnapDraw
        </div>
        {org && <div className="sd-label mt-1">{org}</div>}
      </div>
    </div>
  );
}

const STATUS_STYLES = {
  draft: "text-status-draft",
  pending: "text-status-pending",
  sent: "text-status-overdue",
  accepted: "text-status-accepted",
  won: "text-status-accepted",
  paid: "text-status-accepted",
  overdue: "text-status-overdue",
  closed: "text-status-closed",
  void: "text-status-closed line-through decoration-1",
  superseded: "text-status-closed",
  invoiced: "text-status-closed",
} as const;

export type StatusKey = keyof typeof STATUS_STYLES;

/**
 * `late` is the derived overdue modifier (see @/lib/docStatus's isOverdue) —
 * the chip keeps showing the real stored status and gains an amber rule +
 * day counter, so lists stay scannable by state while amber marks the rows
 * to chase today. There is no "overdue" status to set; only this to display.
 */
export function StatusChip({ status, late = 0 }: { status: StatusKey; late?: number }) {
  const t = useT();
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className={cn(
          "inline-flex items-center rounded-md border border-current px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.16em]",
          STATUS_STYLES[status],
          late > 0 && "rounded-l-none border-l-0 pl-2",
        )}
      >
        {t(`status.${status}`)}
      </span>
      {late > 0 ? <span className="order-first h-6 w-0.5 rounded-full bg-warning" aria-hidden /> : null}
      {late > 0 ? (
        <span className="whitespace-nowrap font-mono text-[10px] uppercase tracking-[0.16em] text-warning">
          {t("docs.late", { days: late })}
        </span>
      ) : null}
    </span>
  );
}
